import { Check, Microphone01 } from "@untitledui/icons"

import { cx } from "@/components/ui/cx"
import { mmss } from "@/lib/duration"
import type { SessionState } from "@/pages/admin/calls/types"

import { TERMINAL_LABELS } from "./copy"

/*
 * One pill, every session state. Certainty grammar drives it, never a hue:
 *
 *   remote     dashed print edge + print.600 pulse — not yet real (dialling, waiting, calling back)
 *   live       filled ink, a paper dot pulsing, a tabular timer — the call, or the memo, is happening
 *   outline    solid ink hairline, no mark — the school has gone, the prompt is playing
 *   system     the plan well + ink@52 dot — the system's turn; safe to leave
 *   confirmed  do.500 tick, do.800 word — done
 *   caution    caution ground, .500 dot, .700 word — a person must act
 *   settled    hairline, the word at 65% — ended short, nothing to do
 */
type Shape =
  | "remote"
  | "live"
  | "outline"
  | "system"
  | "confirmed"
  | "caution"
  | "settled"

interface Spec {
  shape: Shape
  label: (name: string) => string
  pulse?: boolean
  timer?: "talk" | "memo"
  mark?: "mic" | "tick"
  emphasis?: boolean
}

const SPEC: Record<SessionState, Spec> = {
  created: { shape: "remote", label: () => "Setting up", pulse: true },
  dialing_staffer: { shape: "remote", label: () => "Calling you", pulse: true },
  awaiting_confirmation: {
    shape: "remote",
    label: () => "Press 1 on your phone",
    emphasis: true,
  },
  dialing_contact: {
    shape: "remote",
    label: (name) => `Calling ${name}`,
    pulse: true,
  },
  in_call: {
    shape: "live",
    label: () => "Connected",
    pulse: true,
    timer: "talk",
  },
  memo_prompting: {
    shape: "outline",
    label: (name) => `${name} has left the call`,
  },
  recording_memo: {
    shape: "live",
    label: () => "Recording your memo",
    pulse: true,
    timer: "memo",
    mark: "mic",
  },
  redialing_staffer: {
    shape: "remote",
    label: () => "Calling you back for your memo",
    pulse: true,
  },
  processing: {
    shape: "system",
    label: () => "Working on the transcript",
    pulse: true,
  },
  completed: { shape: "confirmed", label: () => "Done", mark: "tick" },
  needs_attention: { shape: "caution", label: () => "Needs attention" },
  abandoned: { shape: "settled", label: () => TERMINAL_LABELS.abandoned! },
  interrupted: { shape: "settled", label: () => TERMINAL_LABELS.interrupted! },
  failed: { shape: "settled", label: () => TERMINAL_LABELS.failed! },
}

const SHAPE: Record<Shape, string> = {
  remote: "border border-dashed border-print-700 text-secondary",
  live: "bg-ink-950 text-stone-50",
  outline: "border border-ink-950 text-primary",
  system: "well text-quaternary",
  confirmed: "border border-ink-hairline text-do-800",
  caution: "bg-caution-50 text-caution-700",
  settled: "border border-ink-hairline text-quaternary",
}

const DOT: Partial<Record<Shape, string>> = {
  remote: "remote-dot",
  live: "inline-block size-1.5 flex-none rounded-full bg-stone-50",
  system: "system-dot",
  caution: "caution-dot",
}

export interface SessionPillProps {
  state: SessionState
  /** The contact's first name, for "Calling Jane" and "Jane has left the call". */
  contactName?: string | null
  /** In dialing_contact, once the carrier reports the far phone ringing. */
  ringing?: boolean
  /** Seconds on the timer the state carries (talk from the bridge, memo from the tone). */
  seconds?: number | null
  size?: "sm" | "lg"
  className?: string
}

export function SessionPill({
  state,
  contactName,
  ringing = false,
  seconds,
  size = "sm",
  className,
}: SessionPillProps) {
  const spec = SPEC[state]
  const name = contactName?.trim() || "the school"
  const label =
    state === "dialing_contact" && ringing ? "Ringing" : spec.label(name)
  const dot = DOT[spec.shape]
  return (
    <span
      data-state={state}
      className={cx(
        "tracking-chip inline-flex max-w-full items-center gap-1.5 rounded-full font-mono font-medium",
        size === "sm" ? "text-2xs h-6 px-2.5" : "h-8 px-3.5 text-xs",
        SHAPE[spec.shape],
        spec.emphasis && "font-semibold",
        className,
      )}
    >
      {spec.mark === "tick" && (
        <Check aria-hidden="true" className="text-do-500 size-3.5 flex-none" />
      )}
      {spec.mark === "mic" && (
        <Microphone01 aria-hidden="true" className="size-3.5 flex-none" />
      )}
      {dot && (
        <span
          aria-hidden="true"
          className={cx(dot, spec.pulse && "animate-pulse")}
        />
      )}
      <span className="truncate">{label}</span>
      {spec.timer && seconds != null && (
        <span className="tabular-nums opacity-80">{mmss(seconds)}</span>
      )}
    </span>
  )
}
